/**
 * Rich device info collection — ported from v1.
 *
 * Collects hardware/software metadata about the current device.
 * Uses Capacitor Device plugin on native platforms, falls back to
 * navigator/screen APIs on web.
 *
 * Returns a Partial<DbDevice>-compatible object (without id, owner, realmId).
 */

import { Capacitor } from "@capacitor/core"
import type { DbDevice } from "@/db/schema"
import { APP_VERSION } from "@/config/constants"

/**
 * Detect OS from userAgent string (web fallback).
 */
function detectOS(): DbDevice["operatingSystem"] {
  const ua = navigator.userAgent
  if (/iPad|iPhone|iPod/.test(ua)) return "ios"
  if (/Android/.test(ua)) return "android"
  if (/Windows/.test(ua)) return "windows"
  if (/Mac/.test(ua)) return "mac"
  return "unknown"
}

/**
 * Collect full device info. Async because Capacitor Device APIs are async.
 */
export async function getFullDeviceInfo(): Promise<Partial<DbDevice>> {
  const platform = Capacitor.getPlatform()
  const userAgent = navigator.userAgent
  const resolvedTimezone =
    typeof Intl !== "undefined" && Intl.DateTimeFormat
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : undefined

  // Base info available on all platforms
  const base: Partial<DbDevice> = {
    platform,
    userAgent,
    language: navigator.language || undefined,
    timezone: resolvedTimezone,
    appVersion: APP_VERSION,
    operatingSystem: detectOS(),
  }

  // Try Capacitor Device plugin for rich native info
  if (Capacitor.isNativePlatform()) {
    try {
      const { Device } = await import("@capacitor/device")
      const info = await Device.getInfo()
      const langRes = await Device.getLanguageCode()

      base.model = info.model
      base.operatingSystem = info.operatingSystem
      base.osVersion = info.osVersion
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      base.iOSVersion = (info as any).iOSVersion
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      base.androidSDKVersion = (info as any).androidSDKVersion
      base.manufacturer = info.manufacturer
      base.isVirtual = info.isVirtual ? 1 : 0
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      base.memUsed = (info as any).memUsed
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      base.webViewVersion = (info as any).webViewVersion
      base.language = langRes.value || navigator.language || undefined

      // Get native build number for diagnostics (appVersion stays as JS-bundled version)
      try {
        const { App } = await import("@capacitor/app")
        const appInfo = await App.getInfo()
        // appVersion intentionally NOT overridden — the JS-bundled __APP_VERSION__
        // from version.json is the single source of truth across all platforms.
        base.appBuild = appInfo.build
      } catch {
        // App.getInfo() not available on web
      }
    } catch {
      // Device plugin not available, use base info
    }
  }

  return base
}
